<script setup>
import BaseBadge from '@/components/Base/BaseBadge.vue';
import BaseChartBar from '@/components/Base/BaseChartBar.vue';
import BaseTooltip from '@/components/Base/BaseTooltip.vue';
import { computed, ref } from 'vue';
import { ratingUtils } from '@/utils';

const props = defineProps({
  skill: {
    type: Object,
    required: true,
  },
  rates: {
    type: Array,
    required: false,
    default: () => [],
  },
  showProficiency: {
    type: Boolean,
    default: true,
  },
  showExperience: {
    type: Boolean,
    default: true,
  },
  labelClass: {
    type: String,
    required: false,
    default: '',
  },
});

const skillRef = ref(null);

const verdict = computed(() => {
  const { rates, skill } = props;
  return rates.length
    ? ratingUtils.calculateVerdict(skill.years * 12, props.rates)
    : false;
});

const color = computed(() => {
  const colors = {
    low: 'error',
    typical: 'gray',
    high: 'success',
  };

  return colors[verdict.value];
});

const proficiency = computed(() => {
  const levels = ['Basic', 'Intermediate', 'Advanced', 'Expert'];

  return levels[props.skill.proficiency_level - 1];
});
</script>

<template>
  <BaseBadge
    badge-class="flex bg-proxify-gray-100 skill-badge h-auto select-none"
  >
    <template
      v-if="$slots.prepend"
      #prepend
    >
      <slot name="prepend" />
    </template>
    <div
      class="flex items-center justify-between text-proxify-gray-600 text-body-sm flex-wrap gap-1 w-full"
      :class="labelClass"
    >
      <div class="flex flex-row items-center gap-1 max-w-full">
        <div class="flex truncate w-full">
          <div class="flex-1 min-w-0">
            <div
              ref="skillRef"
              :class="{
                truncate: skillRef?.offsetWidth < skillRef?.scrollWidth,
              }"
            >
              {{ skill.skill?.name }}
            </div>
          </div>
          <div
            v-if="(!showProficiency || !showExperience) && skill.years"
            class="whitespace-nowrap ml-1"
          >
            - {{ skill.years }}y
          </div>
          <BaseTooltip
            v-if="(!showProficiency || !showExperience) && proficiency"
            :offset="[0, 6]"
          >
            {{
              [proficiency, `${skill.years}y`]
                .filter(
                  (_, i) =>
                    [
                      !showProficiency && proficiency,
                      !showExperience && skill.years,
                    ][i]
                )
                .join(' — ')
            }}
          </BaseTooltip>
        </div>
      </div>
      <div
        data-testid="experience-proficiency"
        class="flex-grow flex items-center justify-end"
      >
        <span v-if="showProficiency">
          <span v-if="skill.years">{{ skill.years }}y</span>
          <span v-if="skill.years && proficiency">-</span>
          <span v-if="proficiency">{{ proficiency }}</span>
        </span>
        <BaseBadge
          v-if="verdict"
          size="sm"
          :color="color"
          icon="info-circle"
          icon-position="right"
          class="capitalize !text-body-sm !font-medium !ml-2 !-mr-2 mix-blend-multiply"
          clickable
          :outline="false"
          data-testid="verdict-badge"
        >
          <span>
            {{ verdict }}
          </span>
          <BaseTooltip
            max-width="216px"
            :offset="[0, 10]"
          >
            <BaseChartBar
              :value="skill.years * 12"
              :rates="rates"
              unit="months"
              :description="`{verdict} experience in ${skill.skill.name} compared to similar profiles from the network`"
            ></BaseChartBar>
          </BaseTooltip>
        </BaseBadge>
      </div>
    </div>
  </BaseBadge>
</template>
